import React, {useEffect, useMemo, useRef, useState} from "react";
import {Link, useParams, useNavigate} from "react-router-dom";
import {
  Alert, Anchor, Badge, Box, Breadcrumbs, Button, Container, Flex,
  Group, Loader, Text, Title,
} from "@mantine/core";
import {IconAlertTriangle, IconArrowLeft, IconCheck, IconDeviceFloppy, IconInfoCircle, IconX} from "@tabler/icons-react";
import axios from "axios";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor,
  MouseSensor, TouchSensor, useSensor, useSensors, closestCenter,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {SortableContext, rectSortingStrategy, useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {HeaderTabs} from "../../../Layout/HeaderTabs";
import useGalleryPhotos from "../hooks/useGalleryPhotos";
import useGallery from "../hooks/useGallery";
import useAuth from "../../../../utils/auth";
import config from "../../../../config";
import {useReorder, ReorderItem} from "./useReorder";
import {timeToString} from "../../../../utils/dateToString";
import classes from "./reorder.module.css";

interface ThumbProps {
  item: ReorderItem
  slug: string
  index: number
  isSelected: boolean
  isDraggingGroup: boolean
  onClick: (e: React.MouseEvent | React.PointerEvent, id: number) => void
  onLongPress: (id: number) => void
}

const LONG_PRESS_MS = 250          // my long-press → enter select mode
const TOUCH_DRAG_DELAY_MS = 300    // dnd-kit TouchSensor → activate drag
const MOVE_THRESHOLD_PX = 8

const PhotoThumb: React.FC<ThumbProps> = ({item, slug, index, isSelected, isDraggingGroup, onClick, onLongPress}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: item.id})
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressed = useRef(false)
  const startPos = useRef<{x: number, y: number} | null>(null)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || (isDraggingGroup && isSelected) ? 0.4 : 1,
  }

  const startLongPress = (clientX: number, clientY: number) => {
    longPressed.current = false
    startPos.current = {x: clientX, y: clientY}
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
    longPressTimer.current = setTimeout(() => {
      longPressed.current = true
      onLongPress(item.id)
    }, LONG_PRESS_MS)
  }
  const cancelLongPress = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }
  const checkMoveThreshold = (clientX: number, clientY: number) => {
    if (!startPos.current) return
    const dx = clientX - startPos.current.x, dy = clientY - startPos.current.y
    if (Math.hypot(dx, dy) > MOVE_THRESHOLD_PX) cancelLongPress()
  }

  // Compose dnd-kit's listeners with our own, so both run on the same event.
  const dndListeners = listeners ?? {}
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    startLongPress(e.clientX, e.clientY)
    ;(dndListeners as any).onPointerDown?.(e)
  }
  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0]
    if (t) startLongPress(t.clientX, t.clientY)
    ;(dndListeners as any).onTouchStart?.(e)
  }
  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0]
    if (t) checkMoveThreshold(t.clientX, t.clientY)
    ;(dndListeners as any).onTouchMove?.(e)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    checkMoveThreshold(e.clientX, e.clientY)
    ;(dndListeners as any).onPointerMove?.(e)
  }
  const handleClick = (e: React.MouseEvent) => {
    cancelLongPress()
    if (longPressed.current) { longPressed.current = false; return }
    onClick(e, item.id)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${classes.thumb} ${isSelected ? classes.selected : ""}`}
      {...attributes}
      {...dndListeners}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={cancelLongPress}
      onTouchCancel={cancelLongPress}
      onClick={handleClick}
    >
      <img
        src={`${config.apiURL}/photos/${slug}/${item.path}/300`}
        alt={item.name}
        className={classes.thumbImg}
        loading="lazy"
        draggable={false}
      />
      <div className={classes.thumbIndex}>{index + 1}</div>
      {item.unreliable ? (
        <div className={classes.thumbBadge}>
          <IconAlertTriangle size={12}/> {item.time_source === "whatsapp" ? "WhatsApp" : "?"}
        </div>
      ) : null}
      {isSelected ? (
        <div className={classes.thumbCheck}><IconCheck size={14}/></div>
      ) : null}
      <div className={classes.thumbTime}>{timeToString(Date.parse(item.sort_time).toString())}</div>
    </div>
  )
}

const GalleryReorder: React.FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  const id = params.id ? parseInt(params.id) : null
  const auth = useAuth()
  const queryClient = useQueryClient()
  const {gallery} = useGallery(id)
  const photos = useGalleryPhotos(id)

  const slug = photos.data?.slug || ""
  const reorder = useReorder(photos.data?.photos || [])
  const [dirty, setDirty] = useState(false)
  const [activeId, setActiveId] = useState<number | null>(null)
  const pendingChanges = useRef<Map<number, string>>(new Map())

  // Reset whenever the underlying data arrives.
  useEffect(() => {
    if (photos.data?.photos) {
      reorder.reset(photos.data.photos)
      pendingChanges.current.clear()
      setDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.data?.photos])

  const sensors = useSensors(
    useSensor(MouseSensor, {activationConstraint: {distance: MOVE_THRESHOLD_PX}}),
    useSensor(TouchSensor, {activationConstraint: {delay: TOUCH_DRAG_DELAY_MS, tolerance: MOVE_THRESHOLD_PX}}),
    useSensor(KeyboardSensor),
  )

  const handleClick = (e: React.MouseEvent | React.PointerEvent, photoId: number) => {
    const mouseEvent = e as React.MouseEvent
    if (mouseEvent.shiftKey) {
      reorder.selectRange(photoId)
    } else if (mouseEvent.ctrlKey || mouseEvent.metaKey || reorder.selectMode) {
      reorder.toggle(photoId)
    } else {
      // plain click: clear selection so user can re-select
      reorder.clearSelection()
    }
  }

  const handleLongPress = (photoId: number) => {
    reorder.setSelectMode(true)
    if (!reorder.selected.has(photoId)) reorder.toggle(photoId)
  }

  const handleDragStart = (e: DragStartEvent) => {
    const id = e.active.id as number
    // If the dragged photo isn't part of the selection, treat it as a single-photo drag.
    if (!reorder.selected.has(id)) {
      reorder.clearSelection()
      reorder.toggle(id)
    }
    setActiveId(id)
  }

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null)
    const {active, over} = e
    if (!over || active.id === over.id) return
    const activeIdx = reorder.indexById.get(active.id as number)
    const overIdx = reorder.indexById.get(over.id as number)
    if (activeIdx === undefined || overIdx === undefined) return

    // Match dnd-kit/sortable default direction: dragging down → drop AFTER over-item;
    // dragging up → drop BEFORE over-item (= after over-1).
    const insertPointOriginalIdx = activeIdx < overIdx ? overIdx : overIdx - 1

    // Translate the original-coords insert point into "staying-list" coords.
    const stayingIndices: number[] = []
    reorder.items.forEach((p, i) => { if (!reorder.selected.has(p.id)) stayingIndices.push(i) })
    let targetInStaying = -1
    for (let i = 0; i < stayingIndices.length; i++) {
      if (stayingIndices[i] <= insertPointOriginalIdx) targetInStaying = i
      else break
    }
    const changes = reorder.moveSelectionTo(targetInStaying)
    if (changes.length) {
      changes.forEach(c => pendingChanges.current.set(c.id, c.sort_time))
      setDirty(true)
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const sort = Array.from(pendingChanges.current.entries()).map(([id, sort_time]) => ({id, sort_time}))
      if (sort.length === 0) return
      await axios.post(
        `${config.apiURL}/api/galleries/${id}/sort`,
        {sort},
        {headers: {Authorization: `Bearer ${auth.apiKey}`}},
      )
    },
    onSuccess: () => {
      pendingChanges.current.clear()
      setDirty(false)
      queryClient.invalidateQueries({queryKey: ["gallery", id]})
      navigate(`/galeries/${id}`)
    },
  })

  const activeItem = activeId !== null ? reorder.items.find(p => p.id === activeId) : null
  const dragGroupCount = reorder.selected.size > 0 && activeId !== null && reorder.selected.has(activeId)
    ? reorder.selected.size : 1

  const unreliableCount = useMemo(
    () => reorder.items.filter(p => p.unreliable).length,
    [reorder.items],
  )

  return (
    <>
      <HeaderTabs/>
      <Container size="md" mb={120}>
        <Breadcrumbs mb={10}>
          <Link to="/"><Anchor>Dashboard</Anchor></Link>
          <Link to="/galeries"><Anchor>Galerie</Anchor></Link>
          {gallery ? <Link to={`/galeries/${gallery.id}`}><Anchor>{gallery.title} ({gallery.date})</Anchor></Link> : null}
        </Breadcrumbs>

        <Flex justify="space-between" align="flex-start" mt="md" mb="md">
          <Box>
            <Title order={1} size="1.5rem" fw="bold">Přeřadit fotky</Title>
            <Text c="dimmed" size="sm">{gallery?.title}</Text>
          </Box>
          <Link to={`/galeries/${id}`}>
            <Button variant="light" color="gray" leftSection={<IconArrowLeft size={16}/>}>Zpět</Button>
          </Link>
        </Flex>

        <Alert variant="light" color="blue" icon={<IconInfoCircle/>} mb="md">
          <Text size="sm">
            <strong>Desktop:</strong> Ctrl/Cmd+klik vybere více fotek, Shift+klik vybere rozsah, pak některou z vybraných přetáhni na nové místo.
            {" "}
            <strong>Mobil:</strong> dlouze podrž fotku pro výběr, klepej pro přidání dalších, pak přetáhni libovolnou z vybraných.
          </Text>
          {unreliableCount > 0 ? (
            <Text size="sm" mt="xs">
              <IconAlertTriangle size={14} style={{verticalAlign: "middle"}}/>{" "}
              {unreliableCount} fotek má nejisté datum (WhatsApp / chybějící EXIF) – přesuň je ručně na správné místo.
            </Text>
          ) : null}
        </Alert>

        {photos.isLoading ? (
          <Group justify="center" mt="xl"><Loader/></Group>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext items={reorder.items.map(p => p.id)} strategy={rectSortingStrategy}>
              <div className={classes.grid}>
                {reorder.items.map((item, idx) => (
                  <PhotoThumb
                    key={item.id}
                    item={item}
                    slug={slug}
                    index={idx}
                    isSelected={reorder.selected.has(item.id)}
                    isDraggingGroup={activeId !== null}
                    onClick={handleClick}
                    onLongPress={handleLongPress}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeItem ? (
                <div className={classes.dragOverlay}>
                  <img
                    src={`${config.apiURL}/photos/${slug}/${activeItem.path}/300`}
                    alt={activeItem.name}
                    className={classes.thumbImg}
                  />
                  {dragGroupCount > 1 ? (
                    <Badge color="blue" size="lg" className={classes.dragCount}>+{dragGroupCount - 1}</Badge>
                  ) : null}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Container>

      <Box className={classes.actionBar}>
        <Container size="md">
          <Group justify="space-between">
            <Text size="sm" c={reorder.selected.size > 0 ? "blue" : "dimmed"}>
              {reorder.selected.size > 0
                ? `Vybráno ${reorder.selected.size} fotek`
                : "Klikni nebo dlouze podrž fotku pro výběr"}
            </Text>
            <Group gap="xs">
              {reorder.selected.size > 0 ? (
                <Button variant="subtle" color="gray" leftSection={<IconX size={14}/>} size="sm" onClick={reorder.clearSelection}>
                  Zrušit výběr
                </Button>
              ) : null}
              <Button
                color="green"
                leftSection={<IconDeviceFloppy size={16}/>}
                disabled={!dirty || saveMutation.isPending}
                loading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                Uložit pořadí
              </Button>
            </Group>
          </Group>
        </Container>
      </Box>
    </>
  )
}

export default GalleryReorder
